import React, { useEffect, useState } from "react";
import { api, formatApiError, fileImageUrl } from "../lib/api";
import { Upload, Trash2, Film } from "lucide-react";
import { toast } from "sonner";
import { GENRES, CONTENT_TYPES } from "../lib/constants";

export default function Admin() {
  const [movies, setMovies] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({
    title: "",
    synopsis: "",
    genre: "Films restaurés HD · Domaine public",
    content_type: "film",
    year: 2024,
    duration_min: 120,
    cast: "",
    director: "",
    is_4k: true,
    is_hdr: false,
    is_dolby_atmos: false,
    rating: "TP",
    tags: "",
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [backdrop, setBackdrop] = useState(null);
  const [video, setVideo] = useState(null);

  const load = async () => {
    const { data } = await api.get("/movies");
    setMovies(data);
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!thumbnail || !video) {
      toast.error("Miniature et vidéo requises");
      return;
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("thumbnail", thumbnail);
    if (backdrop) fd.append("backdrop", backdrop);
    fd.append("video", video);

    setUploading(true);
    setProgress(0);
    try {
      await api.post("/movies", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success(`"${form.title}" publié`);
      setForm({ ...form, title: "", synopsis: "", cast: "", director: "" });
      setThumbnail(null);
      setBackdrop(null);
      setVideo(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'upload");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce film ?")) return;
    try {
      await api.delete(`/movies/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      console.error(err);
      toast.error("Erreur");
    }
  };

  return (
    <div className="pt-24 pb-24 max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
      <div className="mb-10 space-y-2">
        <div className="font-mono-mini text-[11px] uppercase tracking-[0.4em] text-[color:var(--ns-accent)]">
          Studio Admin
        </div>
        <h1 className="font-display text-5xl uppercase">Publication 4K</h1>
        <p className="text-zinc-400 text-sm max-w-xl">
          Uploadez vos films en qualité maximale avec miniatures personnalisées.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <form onSubmit={submit} className="space-y-5 bg-[color:var(--ns-card)] border border-white/10 rounded-2xl p-6">
          <h2 className="font-display text-2xl uppercase flex items-center gap-2">
            <Upload size={18} /> Nouveau film
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Titre">
              <input data-testid="admin-title" value={form.title} onChange={(e) => onChange("title", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Type">
              <select data-testid="admin-type" value={form.content_type} onChange={(e) => onChange("content_type", e.target.value)} className={inputCls}>
                {CONTENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label.replace(/s$/, "")}</option>
                ))}
              </select>
            </Field>
            <Field label="Genre">
              <select data-testid="admin-genre" value={form.genre} onChange={(e) => onChange("genre", e.target.value)} required className={inputCls}>
                {GENRES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </Field>
            <Field label="Année">
              <input data-testid="admin-year" type="number" value={form.year} onChange={(e) => onChange("year", parseInt(e.target.value, 10))} className={inputCls} />
            </Field>
            <Field label="Durée (min)">
              <input data-testid="admin-duration" type="number" value={form.duration_min} onChange={(e) => onChange("duration_min", parseInt(e.target.value, 10))} className={inputCls} />
            </Field>
            <Field label="Réalisateur">
              <input data-testid="admin-director" value={form.director} onChange={(e) => onChange("director", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Classification">
              <select data-testid="admin-rating" value={form.rating} onChange={(e) => onChange("rating", e.target.value)} className={inputCls}>
                <option value="TP">Tous publics</option>
                <option value="-12">-12</option>
                <option value="-16">-16</option>
                <option value="-18">-18</option>
              </select>
            </Field>
          </div>
          <Field label="Casting">
            <input data-testid="admin-cast" value={form.cast} onChange={(e) => onChange("cast", e.target.value)} placeholder="Noms séparés par virgule" className={inputCls} />
          </Field>
          <Field label="Tags (ambiance, séparés par virgule)">
            <input data-testid="admin-tags" value={form.tags} onChange={(e) => onChange("tags", e.target.value)} placeholder="Horreur, Thriller, Science-Fiction…" className={inputCls} />
          </Field>
          <Field label="Synopsis">
            <textarea data-testid="admin-synopsis" value={form.synopsis} onChange={(e) => onChange("synopsis", e.target.value)} rows={4} className={inputCls} />
          </Field>

          <div className="grid sm:grid-cols-3 gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input id="is4k" data-testid="admin-is4k" type="checkbox" checked={form.is_4k} onChange={(e) => onChange("is_4k", e.target.checked)} />
              <span className="font-mono-mini text-xs uppercase tracking-widest">4K Ultra HD</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="admin-ishdr" type="checkbox" checked={form.is_hdr} onChange={(e) => onChange("is_hdr", e.target.checked)} />
              <span className="font-mono-mini text-xs uppercase tracking-widest">HDR10</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="admin-isatmos" type="checkbox" checked={form.is_dolby_atmos} onChange={(e) => onChange("is_dolby_atmos", e.target.checked)} />
              <span className="font-mono-mini text-xs uppercase tracking-widest">Dolby Atmos</span>
            </label>
          </div>

          <Field label="Miniature (poster) — image">
            <input data-testid="admin-thumbnail" type="file" accept="image/*" onChange={(e) => setThumbnail(e.target.files?.[0] || null)} required className="text-sm" />
          </Field>
          <Field label="Backdrop (image large, optionnel)">
            <input data-testid="admin-backdrop" type="file" accept="image/*" onChange={(e) => setBackdrop(e.target.files?.[0] || null)} className="text-sm" />
          </Field>
          <Field label="Fichier film (mp4 recommandé)">
            <input data-testid="admin-video" type="file" accept="video/*" onChange={(e) => setVideo(e.target.files?.[0] || null)} required className="text-sm" />
          </Field>

          {uploading && (
            <div className="space-y-2">
              <div className="font-mono-mini text-xs text-zinc-400 uppercase tracking-widest">
                Upload en cours… {progress}%
              </div>
              <div className="h-1.5 bg-white/10 rounded-full">
                <div className="h-full bg-[color:var(--ns-accent)] rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <button data-testid="admin-submit" disabled={uploading} type="submit" className="w-full py-3 btn-accent rounded-lg font-semibold uppercase tracking-widest text-sm disabled:opacity-60">
            {uploading ? "Publication…" : "Publier le film"}
          </button>
        </form>

        <div className="space-y-4">
          <h2 className="font-display text-2xl uppercase flex items-center gap-2">
            <Film size={18} /> Catalogue ({movies.length})
          </h2>
          <div className="space-y-3 max-h-[800px] overflow-y-auto pr-2">
            {movies.map((m) => (
              <div key={m.id} data-testid={`admin-movie-${m.id}`} className="flex gap-3 bg-[color:var(--ns-card)] border border-white/10 rounded-xl p-3">
                {m.thumbnail_path && (
                  <img src={fileImageUrl(m.thumbnail_path)} alt={m.title} className="w-20 h-28 object-cover rounded-lg" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase truncate">{m.title}</div>
                  <div className="font-mono-mini text-[11px] text-zinc-400 uppercase tracking-widest">
                    {m.genre} · {m.year} · {m.duration_min} min
                  </div>
                  <div className="text-xs text-zinc-500 line-clamp-2 mt-1">{m.synopsis}</div>
                </div>
                <button
                  data-testid={`admin-delete-${m.id}`}
                  onClick={() => del(m.id)}
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-[color:var(--ns-accent)] flex items-center justify-center transition"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {movies.length === 0 && <div className="text-zinc-500 text-sm">Aucun film pour le moment.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg outline-none focus:border-[color:var(--ns-accent)] transition text-sm";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-mono-mini text-[11px] uppercase tracking-widest text-zinc-400">{label}</label>
      {children}
    </div>
  );
}
